import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Grid,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Paper,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  FileCopy,
  Preview,
  Save,
  Twitter,
  Facebook,
  Instagram,
  VideoLibrary,
} from '@mui/icons-material';
import { socialMediaApi } from '../../api/socialMediaApi';

interface Template {
  templateId: string;
  name: string;
  contentType: string;
  platforms: string[];
  content: {
    text?: string;
    hashtags?: string[];
    variables?: string[];
    imageCount?: number;
    videoSettings?: {
      duration: number;
      style: string;
    };
  };
  createdAt: string;
  updatedAt: string;
  usageCount: number;
}

interface TemplateManagerProps {
  onAlert: (type: 'success' | 'error' | 'info', message: string) => void;
}

const TemplateManager: React.FC<TemplateManagerProps> = ({ onAlert }) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    contentType: 'daily-reveal',
    platforms: [] as string[],
    text: '',
    hashtags: '',
    variables: '',
    imageCount: 1,
    videoDuration: 15,
    videoStyle: 'reveal',
  });
  const [previewContent, setPreviewContent] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await socialMediaApi.getTemplates();
      setTemplates(response.templates);
    } catch (error) {
      onAlert('error', 'Failed to fetch templates');
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      const templateData = {
        name: formData.name,
        contentType: formData.contentType,
        platforms: formData.platforms,
        content: {
          text: formData.text,
          hashtags: formData.hashtags.split(' ').filter(tag => tag.length > 0),
          variables: formData.variables.split(',').map(v => v.trim()).filter(v => v.length > 0),
          imageCount: formData.imageCount,
          videoSettings: formData.contentType === 'video' ? {
            duration: formData.videoDuration,
            style: formData.videoStyle,
          } : undefined,
        },
      };

      await socialMediaApi.createTemplate(templateData);
      onAlert('success', 'Template created successfully');
      setEditDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      onAlert('error', 'Failed to create template');
      console.error('Error creating template:', error);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      const templateData = {
        name: formData.name,
        contentType: formData.contentType,
        platforms: formData.platforms,
        content: {
          text: formData.text,
          hashtags: formData.hashtags.split(' ').filter(tag => tag.length > 0),
          variables: formData.variables.split(',').map(v => v.trim()).filter(v => v.length > 0),
          imageCount: formData.imageCount,
          videoSettings: formData.contentType === 'video' ? {
            duration: formData.videoDuration,
            style: formData.videoStyle,
          } : undefined,
        },
      };

      await socialMediaApi.updateTemplate(selectedTemplate.templateId, templateData);
      onAlert('success', 'Template updated successfully');
      setEditDialogOpen(false);
      setSelectedTemplate(null);
      resetForm();
      fetchTemplates();
    } catch (error) {
      onAlert('error', 'Failed to update template');
      console.error('Error updating template:', error);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await socialMediaApi.deleteTemplate(templateId);
      onAlert('info', 'Template deleted');
      fetchTemplates();
    } catch (error) {
      onAlert('error', 'Failed to delete template');
      console.error('Error deleting template:', error);
    }
  };

  const handleDuplicateTemplate = async (template: Template) => {
    try {
      const duplicatedTemplate = {
        ...template,
        name: `${template.name} (Copy)`,
      };
      delete (duplicatedTemplate as any).templateId;
      delete (duplicatedTemplate as any).createdAt;
      delete (duplicatedTemplate as any).updatedAt;
      delete (duplicatedTemplate as any).usageCount;

      await socialMediaApi.createTemplate(duplicatedTemplate);
      onAlert('success', 'Template duplicated successfully');
      fetchTemplates();
    } catch (error) {
      onAlert('error', 'Failed to duplicate template');
      console.error('Error duplicating template:', error);
    }
  };

  const handleEdit = (template: Template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      contentType: template.contentType,
      platforms: template.platforms,
      text: template.content.text || '',
      hashtags: template.content.hashtags?.join(' ') || '',
      variables: template.content.variables?.join(', ') || '',
      imageCount: template.content.imageCount || 1,
      videoDuration: template.content.videoSettings?.duration || 15,
      videoStyle: template.content.videoSettings?.style || 'reveal',
    });
    setEditDialogOpen(true);
  };

  const handlePreview = (template: Template) => {
    setSelectedTemplate(template);

    // Generate preview with sample data
    let preview = template.content.text || '';

    // Replace variables with sample values
    if (template.content.variables) {
      template.content.variables.forEach(variable => {
        const sampleValues: { [key: string]: string } = {
          'name': 'John Smith',
          'initials': 'JS',
          'date': new Date().toLocaleDateString(),
          'score': '95',
          'rank': '1st',
          'hints': '3',
        };
        const value = sampleValues[variable.toLowerCase()] || `[${variable}]`;
        preview = preview.replace(new RegExp(`{{${variable}}}`, 'g'), value);
      });
    }

    // Add hashtags
    if (template.content.hashtags && template.content.hashtags.length > 0) {
      preview += '\n\n' + template.content.hashtags.map(tag => `#${tag}`).join(' ');
    }

    setPreviewContent(preview);
    setPreviewDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contentType: 'daily-reveal',
      platforms: [],
      text: '',
      hashtags: '',
      variables: '',
      imageCount: 1,
      videoDuration: 15,
      videoStyle: 'reveal',
    });
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'twitter':
        return <Twitter sx={{ fontSize: 16 }} />;
      case 'facebook':
        return <Facebook sx={{ fontSize: 16 }} />;
      case 'instagram':
        return <Instagram sx={{ fontSize: 16 }} />;
      case 'tiktok':
        return <VideoLibrary sx={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const getContentTypeColor = (contentType: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' } = {
      'daily-reveal': 'primary',
      'carousel': 'secondary',
      'leaderboard': 'success',
      'teaser': 'warning',
    };
    return colors[contentType] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Content Templates ({templates.length})
        </Typography>
        <Button
          startIcon={<Add />}
          onClick={() => {
            setSelectedTemplate(null);
            resetForm();
            setEditDialogOpen(true);
          }}
          variant="contained"
        >
          Create Template
        </Button>
      </Box>

      {templates.length === 0 ? (
        <Alert severity="info">
          No templates created yet. Templates help you maintain consistent content across posts.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {templates.map((template) => (
            <Grid item xs={12} md={6} lg={4} key={template.templateId}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                    <Typography variant="h6" component="div">
                      {template.name}
                    </Typography>
                    <Chip
                      label={template.contentType}
                      color={getContentTypeColor(template.contentType) as any}
                      size="small"
                    />
                  </Box>

                  <Box display="flex" gap={0.5} mb={2}>
                    {template.platforms.map((platform) => (
                      <Chip
                        key={platform}
                        icon={getPlatformIcon(platform) || undefined}
                        label={platform}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    mb: 2,
                  }}>
                    {template.content.text || 'No text content'}
                  </Typography>

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      Used {template.usageCount} times
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Updated {new Date(template.updatedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <IconButton
                    size="small"
                    onClick={() => handlePreview(template)}
                    color="primary"
                  >
                    <Preview />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(template)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDuplicateTemplate(template)}
                  >
                    <FileCopy />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteTemplate(template.templateId)}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit/Create Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedTemplate ? 'Edit Template' : 'Create Template'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Template Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Content Type</InputLabel>
                <Select
                  value={formData.contentType}
                  onChange={(e) => setFormData({ ...formData, contentType: e.target.value })}
                  label="Content Type"
                >
                  <MenuItem value="daily-reveal">Daily Reveal</MenuItem>
                  <MenuItem value="carousel">Carousel</MenuItem>
                  <MenuItem value="leaderboard">Leaderboard</MenuItem>
                  <MenuItem value="teaser">Teaser</MenuItem>
                  <MenuItem value="video">Video</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Image Count"
                value={formData.imageCount}
                onChange={(e) => setFormData({ ...formData, imageCount: parseInt(e.target.value) || 1 })}
                InputProps={{ inputProps: { min: 1, max: 15 } }}
              />
            </Grid>

            {formData.contentType === 'video' && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Video Duration</InputLabel>
                    <Select
                      value={formData.videoDuration}
                      onChange={(e) => setFormData({ ...formData, videoDuration: parseInt(e.target.value as string) })}
                      label="Video Duration"
                    >
                      <MenuItem value={15}>15 seconds</MenuItem>
                      <MenuItem value={30}>30 seconds</MenuItem>
                      <MenuItem value={60}>60 seconds</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Video Style</InputLabel>
                    <Select
                      value={formData.videoStyle}
                      onChange={(e) => setFormData({ ...formData, videoStyle: e.target.value })}
                      label="Video Style"
                    >
                      <MenuItem value="reveal">Reveal</MenuItem>
                      <MenuItem value="carousel">Carousel</MenuItem>
                      <MenuItem value="quiz">Quiz</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Platforms
              </Typography>
              <Box display="flex" gap={1}>
                {['twitter', 'facebook', 'instagram', 'tiktok'].map((platform) => (
                  <FormControlLabel
                    key={platform}
                    control={
                      <Checkbox
                        checked={formData.platforms.includes(platform)}
                        onChange={(e) => {
                          const platforms = e.target.checked
                            ? [...formData.platforms, platform]
                            : formData.platforms.filter(p => p !== platform);
                          setFormData({ ...formData, platforms });
                        }}
                      />
                    }
                    label={platform}
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Content Text"
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                helperText="Use {{variable}} for dynamic content (e.g., {{name}}, {{date}}, {{score}})"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Variables (comma-separated)"
                value={formData.variables}
                onChange={(e) => setFormData({ ...formData, variables: e.target.value })}
                helperText="E.g., name, date, score, rank"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Hashtags (space-separated)"
                value={formData.hashtags}
                onChange={(e) => setFormData({ ...formData, hashtags: e.target.value })}
                helperText="Don't include # symbol. E.g., FamousInitials DailyGame"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={selectedTemplate ? handleUpdateTemplate : handleCreateTemplate}
            variant="contained"
            startIcon={<Save />}
            disabled={!formData.name || formData.platforms.length === 0}
          >
            {selectedTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Template Preview
        </DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {selectedTemplate.name}
              </Typography>
              <Box display="flex" gap={0.5} mb={2}>
                {selectedTemplate.platforms.map((platform) => (
                  <Chip
                    key={platform}
                    icon={getPlatformIcon(platform) || undefined}
                    label={platform}
                    size="small"
                  />
                ))}
              </Box>
              <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {previewContent}
                </Typography>
              </Paper>
              {selectedTemplate.content.imageCount && selectedTemplate.content.imageCount > 1 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  This template includes {selectedTemplate.content.imageCount} images
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateManager;